import React, { useCallback, useEffect, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { CountDetail, CountSummary } from "../../../../services/v2/stockops.service";
import ReasonPrompt from "../../../../components/kits/reasonPrompt";
import ProductPicker from "../products/productPicker";
import { Empty, Loading, apiError, openBlob, ui } from "../kits/shared";
import { Modal, PhotoInput } from "./traslados";

/*
	Bodegas → Conteos (sección "stockCounts"). Contando (a ciegas) → Por firmar (otra persona revisa y firma) →
	Por aprobar → Aplicado (Super Admin; la diferencia entra como saldo inicial o ajuste, con costo de arranque opcional).
*/

const S = { COUNTING: 1, TO_SIGN: 2, TO_APPROVE: 3, APPLIED: 4, CANCELLED: 5 };

const chip = (statusId: number, text: string) => {
	const style = statusId === S.APPLIED ? { color: "var(--wa-verde)", background: "var(--wa-verde-suave)" }
		: statusId === S.CANCELLED ? { color: "var(--wa-apagado)", background: "var(--wa-papel-2)" }
		: statusId === S.TO_APPROVE ? { color: "var(--wa-acento-texto)", background: "var(--wa-acento-suave)" }
		: { color: "var(--wa-tinta)", background: "var(--wa-papel-3)" };
	return <span style={{ ...style, borderRadius: "10px", padding: "1px 9px", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}>{text}</span>;
};

const Conteos: React.FC<{ campusId: number; warehouses: any[]; perms: any; idleLocked: boolean }> = ({ campusId, warehouses, perms, idleLocked }) => {
	const http = HttpManager.getInstance().stockOpsService;
	const [rows, setRows] = useState<CountSummary[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [openId, setOpenId] = useState<number | null>(null);
	const [creating, setCreating] = useState<boolean>(false);

	const load = useCallback(() => {
		http.counts(campusId).then((response) => setRows(response.data)).catch((requestError) => { setRows([]); setError(apiError(requestError, "No se pudieron cargar los conteos.")); });
	}, [campusId]);
	useEffect(() => { load(); }, [load]);

	return <>
		{perms.create ? <div style={{ display: "flex", justifyContent: "flex-end", margin: "0 0 10px" }}>
			<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={idleLocked} onClick={() => setCreating(true)}>Nuevo conteo</button>
		</div> : null}
		<div style={{ ...ui.card, overflowX: "auto" }}>
			{rows === null ? <Loading /> : error ? <div className="alert alert-danger" style={{ margin: "16px" }}>{error}</div>
				: rows.length < 1 ? <Empty text="Sin conteos. El conteo inicial de cada bodega es lo primero." />
				: <table style={{ width: "100%", borderCollapse: "collapse" }}>
					<thead><tr>
						<th style={ui.th}>Conteo</th>
						<th style={ui.th}>Bodega</th>
						<th style={{ ...ui.th, ...ui.right }}>Productos</th>
						<th style={{ ...ui.th, ...ui.right }}>Piezas</th>
						<th style={ui.th}>Contó</th>
						<th style={ui.th}>Firmó</th>
						<th style={ui.th}>Estado</th>
					</tr></thead>
					<tbody>{rows.map((row) => <tr key={row.id} style={{ cursor: "pointer" }} onClick={() => setOpenId(row.id)}>
						<td style={ui.td}><div style={{ fontFamily: "monospace", fontWeight: 700 }}>{row.folio}</div><div style={ui.tiny}>{row.kindLabel} · {row.createdAt}</div></td>
						<td style={ui.td}>{row.warehouse}{row.shelves ? <div style={ui.tiny}>{row.shelves}</div> : null}</td>
						<td style={{ ...ui.td, ...ui.right }}>{row.lineCount}</td>
						<td style={{ ...ui.td, ...ui.right }}>{row.pieces}</td>
						<td style={ui.td}>{row.countedBy}</td>
						<td style={ui.td}>{row.secondCounter || "-"}{row.signedAt ? <div style={ui.tiny}>{row.signedAt}</div> : null}</td>
						<td style={ui.td}>{chip(row.statusId, row.status)}{row.approvedBy ? <div style={ui.tiny}>aplicó {row.approvedBy}</div> : null}</td>
					</tr>)}</tbody>
				</table>}
		</div>
		{creating ? <NewCount campusId={campusId} warehouses={warehouses} onCancel={() => setCreating(false)} onDone={(count) => { setCreating(false); load(); setOpenId(count.id); }} /> : null}
		{openId !== null ? <CountModal campusId={campusId} countId={openId} onChanged={load} onClose={() => setOpenId(null)} /> : null}
	</>;
};

const NewCount: React.FC<{ campusId: number; warehouses: any[]; onDone: (count: CountDetail) => void; onCancel: () => void }> = ({ campusId, warehouses, onDone, onCancel }) => {
	const physical = warehouses.filter((warehouse) => warehouse.kind !== "virtual");
	const [warehouseId, setWarehouseId] = useState<string>(String(physical[0]?.id || ""));
	const [kind, setKind] = useState<string>("initial");
	const [shelves, setShelves] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const save = async () => {
		setBusy(true);
		setError(null);
		try {
			const response = await HttpManager.getInstance().stockOpsService.createCount(campusId, { warehouseId, kind, shelves: shelves.trim() });
			onDone(response.data);
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudo abrir el conteo."));
		} finally {
			setBusy(false);
		}
	};
	return <Modal width="520px" busy={busy} onClose={onCancel}>
		<h4 style={{ marginBottom: "12px" }}>Nuevo conteo</h4>
		<div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
			<div style={ui.field("100%")}><span style={ui.caption}>Bodega</span>
				<select className="form-select" style={ui.input} value={warehouseId} disabled={busy} onChange={(event) => setWarehouseId(event.target.value)}>
					{physical.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
				</select></div>
			<div style={{ display: "flex", gap: "6px" }}>
				<button type="button" className={"btn btn-sm rounded-pill " + (kind === "initial" ? "btn-primary" : "btn-outline-secondary")} disabled={busy} onClick={() => setKind("initial")}>Conteo inicial</button>
				<button type="button" className={"btn btn-sm rounded-pill " + (kind === "cycle" ? "btn-primary" : "btn-outline-secondary")} disabled={busy} onClick={() => setKind("cycle")}>Conteo de revisión</button>
			</div>
			<div style={ui.field("100%")}><span style={ui.caption}>Estantes o zonas que se cuentan</span>
				<input type="text" className="form-control" style={ui.input} maxLength={255} value={shelves} disabled={busy} onChange={(event) => setShelves(event.target.value)} /></div>
		</div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy || warehouseId === ""} onClick={save}>{busy ? "Abriendo…" : "Empezar a contar"}</button>
		</div>
	</Modal>;
};

interface EditLine { articleId: number; name: string; waCode: string | null; barcode: string | null; serialized: boolean; quantity: string; serials: string }

const CountModal: React.FC<{ campusId: number; countId: number; onChanged: () => void; onClose: () => void }> = ({ campusId, countId, onChanged, onClose }) => {
	const http = HttpManager.getInstance().stockOpsService;
	const [count, setCount] = useState<CountDetail | null>(null);
	const [lines, setLines] = useState<EditLine[]>([]);
	const [photos, setPhotos] = useState<File[]>([]);
	const [shelf, setShelf] = useState<string>("");
	const [signer, setSigner] = useState<string>("");
	const [second, setSecond] = useState<Record<number, string>>({});
	const [costs, setCosts] = useState<Record<number, string>>({});
	const [scan, setScan] = useState<string>("");
	const [scanNote, setScanNote] = useState<string | null>(null);
	const [cancelling, setCancelling] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const scanRef = useRef<HTMLInputElement>(null);

	const apply = (data: CountDetail) => {
		setCount(data);
		setLines(data.lines.map((line) => ({ articleId: line.articleId, name: line.name, waCode: line.waCode, barcode: line.barcode, serialized: line.serialized, quantity: String(line.counted), serials: (line.serials || []).join(", ") })));
		setPhotos([]);
	};
	const load = useCallback(() => {
		http.count(campusId, countId).then((response) => apply(response.data)).catch((requestError) => setError(apiError(requestError, "No se pudo abrir el conteo.")));
	}, [campusId, countId]);
	useEffect(() => { load(); }, [load]);

	const run = async (call: () => Promise<any>) => {
		setBusy(true);
		setError(null);
		try {
			const response = await call();
			apply(response.data);
			onChanged();
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudo completar."));
		} finally {
			setBusy(false);
		}
	};

	// El lector escribe el código y Enter: suma una pieza (o agrega el producto si no estaba).
	const onScan = async () => {
		const code = scan.trim();
		setScan("");
		if (!code) return;
		const found = lines.findIndex((line) => (line.barcode || "").toLowerCase() === code.toLowerCase() || (line.waCode || "").toLowerCase() === code.toLowerCase());
		if (found >= 0) {
			setLines(lines.map((line, i) => i === found ? { ...line, quantity: String((Number(line.quantity) || 0) + 1) } : line));
			setScanNote(lines[found].name + ": " + ((Number(lines[found].quantity) || 0) + 1));
			return;
		}
		try {
			const response = await HttpManager.getInstance().inventoryService.list(campusId, { q: code, kinds: ["article", "vitrina"], includeVariants: 1 }, 1, 5);
			const match = (response.data.data || []).filter((product: any) => product.trackStock && ((product.barcode || "").toLowerCase() === code.toLowerCase() || (product.waCode || "").toLowerCase() === code.toLowerCase()));
			if (match.length !== 1) { setScanNote("\"" + code + "\" no se encontró."); return; }
			const product = match[0];
			setLines([...lines, { articleId: product.id, name: product.name, waCode: product.waCode, barcode: product.barcode, serialized: !!product.serialized, quantity: "1", serials: "" }]);
			setScanNote(product.name + ": 1");
		} catch {
			setScanNote("No se pudo buscar el código.");
		}
	};

	if (!count) return <Modal onClose={onClose}>{error ? <div className="alert alert-danger">{error}</div> : <Loading />}</Modal>;
	const st = count.statusId;
	const lineValues = () => lines.map((line) => ({ articleId: line.articleId, quantity: Number(line.quantity) || 0, serials: line.serials.split(/[\s,;]+/).filter(Boolean) }));

	return <Modal width="900px" busy={busy} onClose={onClose}>
		<div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "flex-start" }}>
			<div>
				<h4 style={{ margin: 0, fontFamily: "monospace" }}>{count.folio} {chip(st, count.status)}</h4>
				<div style={{ ...ui.small, marginTop: "4px" }}>{count.kindLabel} · {count.warehouse}{count.shelves ? " · " + count.shelves : ""} · contó {count.countedBy}
					{count.secondCounter ? " · firma " + count.secondCounter : ""}{count.approvedBy ? " · aplicó " + count.approvedBy + " " + count.approvedAt : ""}</div>
			</div>
			<button type="button" className="btn-close" onClick={onClose} disabled={busy} />
		</div>

		{count.canEdit ? <>
			<form style={{ ...ui.field("100%"), marginTop: "12px" }} onSubmit={(event) => { event.preventDefault(); onScan(); }}>
				<span style={ui.caption}>Escanear</span>
				<input ref={scanRef} type="text" className="form-control" style={ui.input} value={scan} disabled={busy} placeholder="Código de barras o código WA" onChange={(event) => setScan(event.target.value)} />
				{scanNote ? <span style={ui.small}>{scanNote}</span> : null}
			</form>
			<table style={{ width: "100%", borderCollapse: "collapse", marginTop: "10px" }}>
				<thead><tr><th style={ui.th}>Producto</th><th style={{ ...ui.th, width: "110px" }}>Contadas</th><th style={{ ...ui.th, width: "30px" }}></th></tr></thead>
				<tbody>{lines.map((line, index) => <tr key={line.articleId}>
					<td style={ui.td}><div style={{ fontWeight: 600 }}>{line.name}</div><div style={ui.tiny}>{line.waCode}</div>
						{line.serialized ? <input type="text" className="form-control" style={{ ...ui.input, marginTop: "4px" }} placeholder="Números de serie separados por coma" value={line.serials} disabled={busy}
							onChange={(event) => setLines(lines.map((entry, i) => i === index ? { ...entry, serials: event.target.value } : entry))} /> : null}</td>
					<td style={ui.td}><input type="number" min={0} className="form-control" style={ui.input} value={line.quantity} disabled={busy}
						onChange={(event) => setLines(lines.map((entry, i) => i === index ? { ...entry, quantity: event.target.value } : entry))} /></td>
					<td style={ui.td}><button type="button" className="btn btn-sm btn-link" style={{ color: "var(--wa-apagado)", padding: 0 }} disabled={busy} onClick={() => setLines(lines.filter((_, i) => i !== index))}>✕</button></td>
				</tr>)}</tbody>
			</table>
			<div style={{ marginTop: "6px" }}>
				<ProductPicker campusId={campusId} kinds={["article", "vitrina"]} excludeIds={lines.map((line) => line.articleId)} disabled={busy} placeholder="Agregar producto (una talla concreta)…"
					onPick={(product: any) => { if (!product.trackStock) { setError("\"" + product.name + "\" tiene tallas: agrega cada talla."); return; } setLines([...lines, { articleId: product.id, name: product.name, waCode: product.waCode, barcode: product.barcode, serialized: !!product.serialized, quantity: "1", serials: "" }]); }} />
			</div>
			<div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "10px", alignItems: "flex-end" }}>
				<div style={ui.field("160px")}><span style={ui.caption}>Estante de estas fotos</span>
					<input type="text" className="form-control" style={ui.input} maxLength={60} value={shelf} disabled={busy} onChange={(event) => setShelf(event.target.value)} /></div>
				<PhotoInput caption="Fotos del estante" photos={photos} busy={busy} onChange={setPhotos} />
				<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={() => run(() => http.saveCountLines(campusId, count.id, lineValues(), photos, shelf.trim()))}>{busy ? "Guardando…" : "Guardar avance"}</button>
			</div>
			<div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "12px", alignItems: "flex-end", borderTop: "1px solid var(--wa-filo)", paddingTop: "10px" }}>
				<div style={ui.field("260px")}><span style={ui.caption}>Quién firma (otra persona)</span>
					<select className="form-select" style={ui.input} value={signer} disabled={busy} onChange={(event) => setSigner(event.target.value)}>
						<option value="">Elige…</option>
						{count.counters.filter((person) => person.id !== count.countedById).map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
					</select></div>
				<button type="button" className="btn btn-primary rounded-pill" disabled={busy || signer === "" || lines.length < 1}
					onClick={() => run(async () => { await http.saveCountLines(campusId, count.id, lineValues(), photos, shelf.trim()); return http.submitCount(campusId, count.id, Number(signer)); })}>Mandar a firma</button>
			</div>
		</> : <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "12px" }}>
			<thead><tr>
				<th style={ui.th}>Producto</th>
				<th style={{ ...ui.th, ...ui.right }}>Contó</th>
				{st === S.TO_SIGN && count.canSign ? <th style={{ ...ui.th, width: "120px" }}>Mi conteo</th> : <th style={{ ...ui.th, ...ui.right }}>Firma</th>}
				{count.lines.some((line) => line.system !== null) ? <><th style={{ ...ui.th, ...ui.right }}>Sistema</th><th style={{ ...ui.th, ...ui.right }}>Diferencia</th></> : null}
				{count.canApprove ? <th style={{ ...ui.th, width: "130px" }}>Costo estimado</th> : count.lines.some((line) => line.unitCost !== null) ? <th style={{ ...ui.th, ...ui.right }}>Costo</th> : null}
			</tr></thead>
			<tbody>{count.lines.map((line) => <tr key={line.id}>
				<td style={ui.td}><div style={{ fontWeight: 600 }}>{line.name}</div><div style={ui.tiny}>{line.waCode}{line.serials.length ? " · " + line.serials.join(", ") : ""}</div></td>
				<td style={{ ...ui.td, ...ui.right }}>{line.counted}</td>
				{st === S.TO_SIGN && count.canSign
					? <td style={ui.td}><input type="number" min={0} className="form-control" style={ui.input} placeholder={String(line.counted)} value={second[line.id] ?? ""} disabled={busy} onChange={(event) => setSecond({ ...second, [line.id]: event.target.value })} /></td>
					: <td style={{ ...ui.td, ...ui.right, color: line.second !== null ? "var(--wa-oro)" : undefined, fontWeight: line.second !== null ? 700 : 400 }}>{line.second !== null ? line.second : (st >= S.TO_APPROVE ? "✓" : "-")}</td>}
				{count.lines.some((entry) => entry.system !== null) ? <>
					<td style={{ ...ui.td, ...ui.right }}>{line.system ?? "-"}</td>
					<td style={{ ...ui.td, ...ui.right, fontWeight: 700, color: (line.difference || 0) < 0 ? "var(--wa-oro)" : undefined }}>{line.difference === null ? "-" : (line.difference > 0 ? "+" : "") + line.difference}</td>
				</> : null}
				{count.canApprove ? <td style={ui.td}><input type="number" min={0} step="0.01" className="form-control" style={ui.input} value={costs[line.id] ?? (line.unitCost ?? "")} disabled={busy}
					onChange={(event) => setCosts({ ...costs, [line.id]: event.target.value })} /></td>
					: count.lines.some((entry) => entry.unitCost !== null) ? <td style={{ ...ui.td, ...ui.right }}>{line.unitCost ?? "-"}</td> : null}
			</tr>)}</tbody>
		</table>}

		{count.canSign ? <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy}
				onClick={() => run(() => http.signCount(campusId, count.id, Object.entries(second).filter(([, value]) => value !== "").map(([lineId, value]) => ({ lineId: Number(lineId), quantity: Number(value) }))))}>Firmar conteo</button>
		</div> : null}
		{count.canApprove ? <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
			<button type="button" className="btn btn-primary rounded-pill" disabled={busy}
				onClick={() => run(() => http.applyCount(campusId, count.id, Object.entries(costs).filter(([, value]) => value !== "").map(([lineId, value]) => ({ lineId: Number(lineId), unitCost: Number(value) }))))}>Aplicar al inventario</button>
		</div> : null}

		{count.files.length > 0 ? <div style={{ marginTop: "12px" }}>
			<div style={{ fontWeight: 700, fontSize: "14px" }}>Fotos</div>
			{count.files.map((file, index) => <button key={file.id} type="button" className="btn btn-link btn-sm" style={{ padding: "0 8px 0 0" }}
				onClick={() => http.countFile(campusId, count.id, file.id).then((response) => openBlob(response.data)).catch(() => setError("No se pudo abrir la foto."))}>{file.shelf || "Foto"} {index + 1}</button>)}
		</div> : null}
		{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		{st < S.APPLIED ? <div style={{ marginTop: "12px" }}><button type="button" className="btn btn-link btn-sm waOroTexto" style={{ padding: 0 }} disabled={busy} onClick={() => setCancelling(true)}>Cancelar conteo</button></div> : null}
		{cancelling ? <ReasonPrompt title={"Cancelar " + count.folio} label="Motivo" confirmLabel="Cancelar conteo" confirmClass="btn-primary" busy={busy} error={error}
			onCancel={() => setCancelling(false)} onConfirm={(reason) => { setCancelling(false); run(() => http.cancelCount(campusId, count.id, reason)); }} /> : null}
	</Modal>;
};

export default Conteos;
